import i18next from 'i18next';
import type { JobEntity } from '@/types/job.types';
import { ROUTES } from '@/app/router/routes';

/** The app language (i18next), read at render time; components re-render on a language change. */
function currentLanguage(): string {
  return i18next.resolvedLanguage || i18next.language || 'en';
}

function isEnglish(language: string): boolean {
  return /^en\b/i.test(language);
}

/** `count` with a long-form unit in `language` (e.g. "2 ans", "3 mois"), or null without Intl support. */
function formatUnit(count: number, unit: 'year' | 'month', language: string): string | null {
  try {
    return new Intl.NumberFormat(language, { style: 'unit', unit, unitDisplay: 'long' }).format(count);
  } catch {
    return null;
  }
}

/**
 * Format experience months as "2Y 3M - 5Y".
 * Mirrors Flutter GenericHelpers.getMonthsToYears(). In English the text is Flutter's exactly; Flutter
 * never translates the Y / M units, so in other languages they are written out in that language
 * ("2 ans 3 mois - 5 ans" in French).
 */
export function formatExperience(
  from?: number | null,
  to?: number | null,
  language: string = currentLanguage(),
): string {
  const fromMonths = from ?? 0;
  const toMonths = to ?? 0;
  const localized = !isEnglish(language);
  const years = (n: number) => (localized && formatUnit(n, 'year', language)) || `${n}Y`;
  const months = (n: number) => (localized && formatUnit(n, 'month', language)) || `${n}M`;

  if (fromMonths === 0 && toMonths === 0) return `${months(0)} ${years(0)}`;

  const toText = (value: number): string => {
    if (value <= 0) return '';
    const whole = Math.floor(value / 12);
    const rest = Math.floor(value % 12);
    return rest > 0 ? `${years(whole)} ${months(rest)}` : years(whole);
  };

  const fromText = toText(fromMonths);
  const toTextValue = toText(toMonths);
  if (fromText && toTextValue) return `${fromText} - ${toTextValue}`;
  return fromText || toTextValue;
}

type Translate = (key: string) => string;

/** A count placeholder a tenant can put in a relative-date translation, e.g. "il y a {n} jours". */
const COUNT_PLACEHOLDER = /\{\{\s*count\s*\}\}|\{\s*(?:n|count)\s*\}|%d|%s/;

/**
 * Relative posted date, e.g. "3 days ago".
 * Mirrors Flutter GenericHelpers.postedDays() — callers prefix it with "Posted".
 *
 * Flutter writes the number in front of the translated phrase ("2 " + 'days ago'). Tenant
 * translations carry no number ('days ago' → "il y a jours"), so outside English that gave
 * "2 il y a jours". The phrase is therefore built as follows:
 *   1. a translation with a count placeholder ({n}, {count}, {{count}}, %d) gets the number there;
 *   2. otherwise, in a language other than English, Intl.RelativeTimeFormat writes it ("il y a 2 jours");
 *   3. otherwise Flutter's "<n> <phrase>".
 */
export function formatPostedDate(
  startDate?: number | string | null,
  t: Translate = (key) => key,
  language: string = currentLanguage(),
): string {
  if (startDate == null || startDate === '') return t('N/A');
  const posted =
    typeof startDate === 'number' || /^\d+$/.test(startDate)
      ? new Date(Number(startDate))
      : new Date(startDate);
  if (isNaN(posted.getTime())) return t('N/A');

  const diffMs = Date.now() - posted.getTime();
  const days = Math.floor(diffMs / 86_400_000);
  const hours = Math.floor(diffMs / 3_600_000);
  const minutes = Math.floor(diffMs / 60_000);
  const unit = (count: number, one: string, many: string, intlUnit: Intl.RelativeTimeFormatUnit) => {
    const phrase = count === 1 ? t(one) : t(many);
    if (COUNT_PLACEHOLDER.test(phrase)) return phrase.replace(COUNT_PLACEHOLDER, String(count));
    if (!isEnglish(language)) {
      try {
        return new Intl.RelativeTimeFormat(language, { numeric: 'always' }).format(-count, intlUnit);
      } catch {
        // unsupported locale — Flutter's composition below
      }
    }
    return `${count} ${phrase}`;
  };

  if (days > 365) return unit(Math.floor(days / 365), 'year ago', 'years ago', 'year');
  if (days > 30) return unit(Math.floor(days / 30), 'month ago', 'months ago', 'month');
  if (days > 7) return unit(Math.floor(days / 7), 'week ago', 'weeks ago', 'week');
  if (days > 0) return unit(days, 'day ago', 'days ago', 'day');
  if (hours > 0) return unit(hours, 'hour ago', 'hours ago', 'hour');
  if (minutes > 0) return unit(minutes, 'minute ago', 'minutes ago', 'minute');
  return t('just now');
}

/**
 * Calendar date, e.g. "12 Sep, 2026".
 * Mirrors Flutter GenericHelpers.getFormattedDate() (DateFormat 'dd MMM, yyyy' in the app language).
 */
export function formatDate(value?: number | null, locale?: string): string {
  if (value == null) return 'N/A';
  const date = new Date(value);
  if (isNaN(date.getTime())) return 'N/A';
  const day = String(date.getDate()).padStart(2, '0');
  let month: string;
  try {
    month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);
  } catch {
    month = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
  }
  return `${day} ${month}, ${date.getFullYear()}`;
}

/**
 * Get the first job location display string.
 */
export function formatJobLocation(entity: JobEntity): string {
  if (entity.jobLocation?.length && entity.jobLocation[0].fqLocationName) {
    return entity.jobLocation[0].fqLocationName;
  }
  return '';
}

/**
 * Build the job share URL.
 * Mirrors Flutter: ${origin}/jobs/${displayId}
 */
export function buildJobShareUrl(displayId: string): string {
  return `${window.location.origin}${ROUTES.JOBS}/${displayId}`;
}

/**
 * Share to a social platform.
 * Mirrors Flutter _shareToSocialPlatform from job_list_view.dart
 */
export function shareToSocialPlatform(
  platform: string,
  shareUrl: string,
  shareText = '🚀 Exciting career opportunities for you. Check out our latest job openings and join a team.',
): void {
  let url = '';

  switch (platform.toLowerCase()) {
    case 'linkedin':
      url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
      break;
    case 'x':
    case 'twitter':
      url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
      break;
    case 'whatsapp':
      url = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;
      break;
    case 'email':
      url = `mailto:?subject=${encodeURIComponent('Career Opportunities for you')}&body=${encodeURIComponent(`${shareText}\n\nCheck it out: ${shareUrl}`)}`;
      break;
    default:
      return;
  }

  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Copy text to the clipboard; resolves to whether it was copied.
 *
 * `text` may be a promise — a link still being built (e.g. waiting for a referral code). The
 * write then starts right away, during the click, through a ClipboardItem that waits for it:
 * Safari only lets a page write to the clipboard while handling the user's click, so writing
 * once the API call had returned was refused. Falls back to writeText, then to a hidden textarea
 * with execCommand('copy') where the Clipboard API is missing or blocked (non-secure origins,
 * older browsers). A rejected promise means nothing was copied.
 */
export async function copyToClipboard(text: string | Promise<string>): Promise<boolean> {
  const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
  if (typeof text !== 'string' && typeof ClipboardItem !== 'undefined' && clipboard?.write) {
    try {
      const blob = text.then((value) => new Blob([value], { type: 'text/plain' }));
      await clipboard.write([new ClipboardItem({ 'text/plain': blob })]);
      return true;
    } catch {
      // Promise items unsupported, or the write was refused — try the plain ways below
    }
  }

  let value: string;
  try {
    value = await text;
  } catch {
    return false;
  }
  try {
    if (clipboard?.writeText) {
      await clipboard.writeText(value);
      return true;
    }
  } catch {
    // Blocked (permissions policy, document not focused…) — fall back
  }
  return copyWithSelection(value);
}

/** Pre-Clipboard-API copy: select the text in a hidden textarea and run the copy command. */
function copyWithSelection(value: string): boolean {
  if (typeof document === 'undefined') return false;
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  try {
    textarea.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    textarea.remove();
  }
}

/**
 * Format string — capitalize first letter, replace underscores.
 * Mirrors Flutter GenericHelpers.formatString()
 */
export function formatString(value?: string): string {
  if (!value) return '';
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
